import { Injectable } from '@angular/core';
import { MapifyMap } from '../models/MapifyMap';
import { MapifyMapTexts } from '../models/MapifyMapTexts';
import { Orientation } from '../models/Orientation';
import { Size } from '../models/Size';
import { StyleService } from './style.service';
import { PrintService } from './print.service';
import { v4 as uuid } from 'uuid';

import { environment } from '../../environments/environment';

declare function unescape(s: string): string;

const getTexts = (styleName: string, orientation: string, finishId: string): MapifyMapTexts => {
  let texts = {};
  const mapStyleConfig = environment.mapify.mapStyle[styleName];

  // get texts for the style, orientation, finish combination
  if (mapStyleConfig && mapStyleConfig[orientation] && mapStyleConfig[orientation][finishId]) {
    if (mapStyleConfig[orientation][finishId].texts) {
      texts = mapStyleConfig[orientation][finishId].texts;
    }
  }

  return texts;
};

@Injectable()
export class MapService {
  constructor(private styleService: StyleService, private printService: PrintService) {}

  getMap(): MapifyMap {
    const defaultStyle = 'classic-granite';
    const defaultFinish = 'classic';
    const defaultOrientation: Orientation = 'portrait';

    const style = this.styleService.list().find(s => s.name === defaultStyle);

    if (style) {
      const {
        colors: { primary, secondary }
      } = style;
      const finish = style.finishes.find(f => f.id === defaultFinish);
      const size: Size = {
        height: 70,
        width: 50,
        unit: 'cm',
        orientation: defaultOrientation
      };

      const print = this.printService.getBySize(size);

      if (finish && print) {
        return {
          id: uuid(),
          version: 2,
          size,
          style,
          zoom: environment.mapify.zoomLevels[12],
          center: {
            lat: 47.365802,
            lng: 8.540688
          },
          finish,
          icons: [],
          frame: null,
          bounds: null,
          texts: getTexts(style.name, defaultOrientation, finish.id),
          printId: print.id
        };
      } else {
        throw new Error('Finish or Print not available');
      }
    } else {
      throw new Error('Style not available');
    }
  }

  loadMap(map: MapifyMap | null): MapifyMap {
    const defaultMap = this.getMap();
    let loadedMap: MapifyMap;

    if (map !== null) {
      try {
        const {
          size: { orientation },
          finish
        } = map;

        map.texts = Object.keys(map.texts).reduce((acc, key) => {
          acc[key].value.next(map.texts[key].value);
          acc[key].font.family.next(map.texts[key].font.family);

          return acc;
        }, getTexts(map.style.name, orientation, finish.id));

        // style contains Gradient which is implemented using classes and can't be directly assigned to a map
        // without implementing a de-serializer!!. To avoid that the style is retrived from styleService
        const style = this.styleService.list().find(s => s.id === map.style.id);

        if (style) {
          loadedMap = Object.assign({}, map, { style });
        } else {
          loadedMap = defaultMap;
        }
      } catch (ex) {
        loadedMap = defaultMap;
      }
    } else {
      loadedMap = defaultMap;
    }

    return loadedMap;
  }

  /**
   * Convert a MapifyMap to a JSON object. This is required due MapifyMap can't be serialized because it contains Observable
   * objects and some other classes.
   */
  toJSON(map: MapifyMap) {
    // create a copy of the map
    const jsonMap = Object.assign({}, map, {
      texts: {}
    });

    // replace Observable instances with their values.
    if (map.texts.title) {
      jsonMap.texts.title = <any>{
        name: map.texts.title.name,
        value: map.texts.title.value.getValue(),
        font: {
          family: map.texts.title.font.family.getValue()
        }
      };
    }

    if (map.texts.subtitle) {
      jsonMap.texts.subtitle = <any>{
        name: map.texts.subtitle.name,
        value: map.texts.subtitle.value.getValue(),
        font: {
          family: map.texts.subtitle.font.family.getValue()
        }
      };
    }

    if (map.texts.description) {
      jsonMap.texts.description = <any>{
        name: map.texts.description.name,
        value: map.texts.description.value.getValue(),
        font: {
          family: map.texts.description.font.family.getValue()
        }
      };
    }

    return jsonMap;
  }

  getThumbnailUrl(serialisedMap: string) {
    const encodedMap = btoa(unescape(encodeURIComponent(serialisedMap.replace(/[^\x00-\x7F]/g, ''))));

    return `${environment.thumbnailApi}/thumbnail?target_dpi=50&map_code=${encodedMap}`;
  }
}



// WEBPACK FOOTER //
// /vercel/workpath0/src/app/services/map.service.ts